import { useState, useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"
import { getAuth, onAuthStateChanged, User as FirebaseUser } from "firebase/auth" // Added FirebaseUser
import { useAuthState } from "react-firebase-hooks/auth" // Added
import { useDocumentData } from "react-firebase-hooks/firestore" // Added
import { doc, setDoc, getFirestore } from "firebase/firestore" // Added setDoc, getFirestore
import { db as firestoreInstance, extractUserInfo } from "@/utils/firebase" // Added db, modified extractUserInfo import
import { useUserSettings } from "@/hooks/useUserSettings"
// import { useData } from "@/context/DataContext" // Removed
import { createEnhancedUserInfo, PUBLIC_PATHS } from "../constants"
import { initializeUserData } from "@/utils/firestore/users/auth"
import { UserData } from "@/context/types" // Assuming UserData is the correct type for user documents

export const useAuthCheck = () => {
  const location = useLocation()
  const auth = getAuth(); // Moved auth initialization up
  const [firebaseUser, loadingAuth, errorAuth] = useAuthState(auth);

  // Get user document from Firestore
  const userDocRef = firebaseUser ? doc(firestoreInstance, 'users', firebaseUser.uid) : null;
  const [currentUserData, loadingUser, errorUser] = useDocumentData(userDocRef);

  const [isLoading, setIsLoading] = useState(true); // Will be derived from loadingAuth and loadingUser
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // const [dataLoaded, setDataLoaded] = useState(false); // Removed, can be derived or handled by parent
  
  const { updateUserData } = useUserSettings(); // This hook might also need refactoring if it uses DataContext

  // Add refs to prevent infinite loops
  const initialAuthCheckCompleteRef = useRef(false);
  const isProcessingAuthChangeRef = useRef(false); // Keep for internal processing lock
  const lastAuthUpdateTimeRef = useRef(0); // Keep for throttling
  const firebaseUserHashRef = useRef(""); // Renamed from userDataHashRef for clarity

  // Check if current path is a public path that shouldn't require authentication
  const isPublicPath = PUBLIC_PATHS.some(
    (path) => location.pathname === path || location.pathname.startsWith(path)
  );

  // Save the current path to sessionStorage when on a protected route to handle reloads
  useEffect(() => {
    if (!isPublicPath && location.pathname !== "/login") {
      sessionStorage.setItem("lastProtectedPath", location.pathname);
    }
  }, [location.pathname, isPublicPath]);

  // This useEffect now handles auth state changes and user data initialization/update
  useEffect(() => {
    if (loadingAuth || (firebaseUser && loadingUser)) {
      setIsLoading(true);
      return;
    }
    
    if (isPublicPath) {
      setIsLoading(false);
      setIsAuthenticated(false); // Not authenticated on public paths for this hook's purpose
      return;
    }

    const processAuthChange = async (currentFirebaseUser: FirebaseUser | null | undefined) => {
      if (isProcessingAuthChangeRef.current) return;

      const newFirebaseUserHash = currentFirebaseUser
        ? JSON.stringify({
            uid: currentFirebaseUser.uid,
            email: currentFirebaseUser.email,
            displayName: currentFirebaseUser.displayName,
          })
        : "";

      if (newFirebaseUserHash === firebaseUserHashRef.current && initialAuthCheckCompleteRef.current) {
        // If firebaseUser object itself hasn't changed, and it's not the initial check,
        // then currentUserData changes (from Firestore listener) should not re-trigger this full flow.
        // However, if currentUserData is a dependency and changes, this effect will run.
        // We need to be careful here.
        // For now, let's assume if firebaseUser is the same, we don't re-process heavily.
        // The main goal is to react to actual auth login/logout.
        setIsLoading(false); // Ensure loading is false if we bail early
        return;
      }
      firebaseUserHashRef.current = newFirebaseUserHash;

      const now = Date.now();
      if (now - lastAuthUpdateTimeRef.current < 120000 && initialAuthCheckCompleteRef.current) {
        setIsLoading(false);
        return;
      }
      
      isProcessingAuthChangeRef.current = true;
      lastAuthUpdateTimeRef.current = now;

      if (currentFirebaseUser) {
        setIsAuthenticated(true);
        const userInfoFromAuth = extractUserInfo(currentFirebaseUser);
        
        // Initialize user data in Firestore if needed (e.g., on first sign-up)
        await initializeUserData(currentFirebaseUser); // This should handle if doc exists

        // currentUserData is the live data from Firestore for this firebaseUser
        // We need to ensure the role from Firestore is used if available
        const roleToUse = currentUserData?.role || userInfoFromAuth.role; // Prioritize Firestore role

        const userInfoWithRole = { ...userInfoFromAuth, role: roleToUse };
        
        const enhancedUserInfo = createEnhancedUserInfo(userInfoWithRole, currentFirebaseUser.uid);

        // Compare with currentUserData from Firestore
        const firestoreUserStr = currentUserData
          ? JSON.stringify({
              id: currentUserData.id,
              email: currentUserData.email,
              firstName: currentUserData.firstName,
              lastName: currentUserData.lastName,
            })
          : "";

        const newUserStr = JSON.stringify({
          id: enhancedUserInfo.id,
          email: enhancedUserInfo.email,
          firstName: enhancedUserInfo.firstName,
          lastName: enhancedUserInfo.lastName,
        });
        
        // Only update if essential parts of enhanced info differ from Firestore,
        // or if Firestore data is missing (currentUserData is null/undefined).
        // This check helps prevent loops if createEnhancedUserInfo adds defaults.
        if (!currentUserData || firestoreUserStr !== newUserStr) {
          try {
            const userRef = doc(firestoreInstance, 'users', currentFirebaseUser.uid);
            await setDoc(userRef, enhancedUserInfo, { merge: true });
            console.log("useAuthCheck: User document updated/created in Firestore.");
          } catch (e) {
            console.error("useAuthCheck: Error writing user document to Firestore:", e);
          }
        }
        
        // updateUserData from useUserSettings might still be called if it handles other aspects
        // of user settings not covered by the main user document, or if it's for a different store.
        // For now, let's assume the main user doc update is sufficient.
        // If useUserSettings updates the same '/users/{uid}' doc, this could be redundant or conflict.
        // This part needs careful review of what useUserSettings does.
        // The setDoc above should be the primary way useAuthCheck ensures the user doc is up-to-date with auth info.
        // Calling updateUserData (from useUserSettings) here can create cycles if useUserSettings
        // also reacts to user document changes.
        // Let's remove this call for now to break a potential loop.
        // The main responsibility of useAuthCheck is to ensure the user doc exists and reflects basic auth info.
        // Detailed profile updates should happen via UserSettings page, not automatically on every auth check.
        // if (!initialAuthCheckCompleteRef.current) {
        //    setTimeout(() => {
        //      updateUserData(enhancedUserInfo as UserData); // Cast to UserData
        //    }, 1000);
        // }

      } else {
        setIsAuthenticated(false);
      }

      initialAuthCheckCompleteRef.current = true;
      setIsLoading(false);
      
      setTimeout(() => {
        isProcessingAuthChangeRef.current = false;
      }, 10000);
    };

    processAuthChange(firebaseUser); // Process the current auth state from useAuthState

    // No need for onAuthStateChanged listener here anymore, as useAuthState handles it.
    // The useEffect will re-run if firebaseUser (from useAuthState) or currentUserData (from useDocumentData) changes.
    // This is the new "subscription".

  }, [firebaseUser, loadingAuth, loadingUser, currentUserData, isPublicPath, location.pathname]); // Removed updateUserData from dependencies

  // Update isLoading based on Firebase hooks
  useEffect(() => {
    if (isPublicPath) {
      setIsLoading(false);
    } else {
      setIsLoading(loadingAuth || (firebaseUser != null && loadingUser));
    }
  }, [loadingAuth, loadingUser, firebaseUser, isPublicPath]);


  return {
    isLoading,
    isAuthenticated,
    // dataLoaded, // Removed, parent should determine this based on its needs
  };
};
